import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { aiApi, stripeConnectApi, memberApi } from '../api';
import { SubscriptionPlan, ActiveFacilityMember } from '../api_types';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import LoadingSpinner from '../shared/components/LoadingSpinner';

interface EmailMarketingProps {
  facilityId: string;
  businessId: string;
}

// Initialize EmailJS with environment variables
const EMAILJS_SERVICE_ID = 'service_2lwbhyj';
const EMAILJS_TEMPLATE_ID = 'template_pba2pgl';
const EMAILJS_PUBLIC_KEY = "ibwKYTVgfm8sstwAW";

// Initialize EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

const SmartMarketingToggle: React.FC<{ onEmailGenerated: (subject: string, content: string) => void }> = ({ onEmailGenerated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [eventDetails, setEventDetails] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateEmail = async () => {
    if (!eventDetails.trim()) {
      setError('Please enter event details');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const { subject, content } = await aiApi.generateEmail(eventDetails);
      onEmailGenerated(subject, content);
      setIsOpen(false);
      setEventDetails('');
    } catch (err) {
      setError('Failed to generate email. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        Smart Marketing
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg p-4 z-10 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Smart Marketing</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="eventDetails" className="block text-sm font-medium text-gray-700">
                Event Details
              </label>
              <textarea
                id="eventDetails"
                value={eventDetails}
                onChange={(e) => setEventDetails(e.target.value)}
                rows={8}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Enter your event details in outline format. For example:
- Event: Summer Fitness Challenge
- Date: June 15, 2024
- Time: 9:00 AM - 12:00 PM
- Location: Main Gym
- Activities: Group workouts, nutrition workshop
- Special Offer: 20% off membership for attendees"
              />
            </div>

            {error && (
              <div className="p-4 bg-red-100 text-red-700 rounded-md">
                {error}
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={generateEmail}
                disabled={isGenerating}
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                  isGenerating
                    ? 'bg-indigo-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
              >
                {isGenerating ? 'Generating...' : 'Generate Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EmailMarketing: React.FC<EmailMarketingProps> = ({ facilityId, businessId }) => {
  const [activeTab, setActiveTab] = useState<'regular' | 'smart'>('regular');
  const [members, setMembers] = useState<ActiveFacilityMember[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailData, setEmailData] = useState({
    subject: '',
    content: '',
    selectedMembers: [] as string[],
    isSending: false,
    sendError: null as string | null,
    sendSuccess: false
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch active members using the Firebase migration API
        const activeMembers = await memberApi.getFacilityMembers(businessId, facilityId);

        // Fetch plans
        console.log('Fetching plans for businessId: ', businessId, ' and facilityId: ', facilityId);
        const { plans: facilityPlans } = await stripeConnectApi.getPlans(businessId, facilityId);

        console.log('Facility plans: ', facilityPlans);

        console.log('Members: ', activeMembers);

        setMembers(activeMembers);
        setPlans(facilityPlans);
      } catch (err) {
        setError('Failed to fetch data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [facilityId, businessId]);

  // Compute which plans are actually assigned to members
  const memberPlanIds = Array.from(new Set(members.map(m => m.planId).filter(Boolean)));
  const plansInUse = plans.filter(plan => memberPlanIds.includes(plan.priceId));

  // Filter members by search, then by plan
  const searchedMembers = search.trim()
    ? members.filter(m => {
        const first = (m.firstName || '').toLowerCase();
        const last = (m.lastName || '').toLowerCase();
        const email = (m.email || '').toLowerCase();
        const full = `${first} ${last}`.trim();
        return (
          first.includes(search.toLowerCase()) ||
          last.includes(search.toLowerCase()) ||
          email.includes(search.toLowerCase()) ||
          full.includes(search.toLowerCase())
        );
      })
    : members;
  const filteredMembers = selectedPlanId === 'all'
    ? searchedMembers
    : searchedMembers.filter(member => member.planId === selectedPlanId);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMemberSelect = (memberId: string) => {
    setEmailData(prev => ({
      ...prev,
      selectedMembers: prev.selectedMembers.includes(memberId)
        ? prev.selectedMembers.filter(id => id !== memberId)
        : [...prev.selectedMembers, memberId]
    }));
  };

  const handleSendEmail = async () => {
    if (!emailData.subject || !emailData.content || emailData.selectedMembers.length === 0) {
      setEmailData(prev => ({ ...prev, sendError: 'Please fill in all fields and select at least one member' }));
      return;
    }

    try {
      setEmailData(prev => ({ ...prev, isSending: true, sendError: null }));
      
      // Get selected members' details
      const selectedMembersDetails = filteredMembers.filter(member => 
        emailData.selectedMembers.includes(member.id)
      );

      // Send email to each selected member
      const emailPromises = selectedMembersDetails.map(member => {
        const templateParams = {
          to_name: `${member.firstName} ${member.lastName}`,
          to_email: member.email,
          subject: emailData.subject,
          message: emailData.content,
          from_name: 'Your Facility Name', // You might want to make this dynamic
          reply_to: 'your-facility@example.com' // Add your facility's reply-to email
        };

        return emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          templateParams
        );
      });

      await Promise.all(emailPromises);

      setEmailData(prev => ({
        ...prev,
        isSending: false,
        sendSuccess: true,
        subject: '',
        content: '',
        selectedMembers: []
      }));
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailData(prev => ({
        ...prev,
        isSending: false,
        sendError: 'Failed to send email. Please try again.'
      }));
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg min-h-screen flex flex-col p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">Marketing</h2>
        <SmartMarketingToggle
          onEmailGenerated={(subject, content) => {
            setEmailData(prev => ({
              ...prev,
              subject,
              content
            }));
          }}
        />
      </div>
      
      {emailData.sendSuccess && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-md">
          Email sent successfully!
        </div>
      )}
      
      {emailData.sendError && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
          {emailData.sendError}
        </div>
      )}

      <div className="space-y-8 flex-1 flex flex-col">
        <div>
          <label htmlFor="subject" className="block text-sm font-bold text-gray-700">
            Subject
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={emailData.subject}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Enter email subject"
          />
        </div>

        <div className="mt-10">
          <label htmlFor="content" className="block text-sm font-bold text-gray-700">
            Content
          </label>
          <textarea
            id="content"
            name="content"
            value={emailData.content}
            onChange={handleInputChange}
            rows={14}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Enter email content"
          />
        </div>

        {/* All controls on the same line */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4 flex-wrap">
          <div className="relative w-full max-w-xs sm:max-w-xs flex-shrink-0">
            <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="pl-8 pr-3 py-1.5 w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
          <div className="hidden sm:block h-7 border-l border-gray-200 mx-2" />
          <div className="flex items-center gap-2 flex-shrink-0">
            <label className="block text-sm font-medium text-gray-700 whitespace-nowrap mr-1">
              Filter by Plan
            </label>
            <select
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              className="w-40 pl-3 pr-8 py-1.5 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
            >
              <option value="all">All Plans</option>
              {plansInUse.map((plan) => (
                <option key={plan.priceId} value={plan.priceId}>
                  {plan.name}
                </option>
              ))}
            </select>
          </div>
          <div className="hidden sm:block h-7 border-l border-gray-200 mx-2" />
          <button
            type="button"
            className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition whitespace-nowrap border border-indigo-600 rounded-none flex-shrink-0"
            onClick={() => {
              if (emailData.selectedMembers.length === filteredMembers.length) {
                setEmailData(prev => ({ ...prev, selectedMembers: [] }));
              } else {
                setEmailData(prev => ({ ...prev, selectedMembers: filteredMembers.map(m => m.id) }));
              }
            }}
          >
            {emailData.selectedMembers.length === filteredMembers.length ? 'Deselect All Members' : 'Select All Members'}
          </button>
        </div>
        <hr className="mb-4 border-gray-200" />

        <div className="max-h-72 overflow-y-auto border rounded-md bg-white">
          {filteredMembers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No members found for this plan.</div>
          ) : (
            filteredMembers.map((member) => {
              const planName = member.planId
                ? plans.find((plan) => plan.priceId === member.planId)?.name || 'N/A'
                : 'N/A';
              return (
                <div
                  key={member.id}
                  className={`p-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                    emailData.selectedMembers.includes(member.id) ? 'bg-indigo-50' : ''
                  }`}
                  onClick={() => handleMemberSelect(member.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {member.firstName} {member.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{member.email}</p>
                      <p className="text-xs text-gray-400">
                        Plan: {planName}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={emailData.selectedMembers.includes(member.id)}
                      onChange={() => handleMemberSelect(member.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex justify-end mt-8">
          <button
            onClick={handleSendEmail}
            disabled={emailData.isSending}
            className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
              emailData.isSending
                ? 'bg-indigo-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
            }`}
          >
            {emailData.isSending ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailMarketing; 